import { useMemoizedFn } from 'ahooks';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Form, Input, Button, Table, DatePicker, Select } from 'tdesign-react';
import useTable from '@/hooks/useTable';
import { getLogsList, deleteLogs } from '@/services/logsService';
import { renderDateTime, renderOptions, renderTags, toTsOptions } from '@/utils/common';
import PermWrapper from '@/components/PermWrapper';
import SelectCollapsed from '@/components/Form/SelectCollapsed';
import { renderResult } from '../OpenApiLogsTable';
import FormModal from './FormModal';

const { FormItem } = Form;

const CLUSTER_TYPE = Object.freeze({
  PULSAR: 'pulsar',
  KOP: 'kop',
});

const clusterTypeOptions = [
  { label: 'Pulsar', value: CLUSTER_TYPE.PULSAR },
  { label: 'Kop', value: CLUSTER_TYPE.KOP },
];

// 操作类型(新增、更新、删除、上传、执行、检查)
const operateTypeOptions = ['新增', '更新', '删除', '上传', '执行', '检查'].map((item) => ({
  label: item,
  value: item,
}));

// 操作来源类型(运维管理、集群管理、平台管理、开发指引、高级功能)
const sourceTypeOptions = ['运维管理', '集群管理', '平台管理', '开发指引', '高级功能'].map(
  (item) => ({ label: item, value: item }),
);

export default function Logs() {
  const [visible, setVisible] = useState(false);

  const columns = [
    {
      title: '用户名',
      colKey: 'username',
      cell: renderTags,
      width: 100,
      fixed: 'left',
    },
    {
      title: '结果码',
      colKey: 'returnCode',
      cell: renderResult,
      width: 80,
    },
    {
      title: '操作详情',
      colKey: 'operateDesc',
      width: 220,
      ellipsis: true,
    },
    {
      title: '集群类型',
      colKey: 'clusterType',
      options: clusterTypeOptions,
      cell: renderOptions,
      width: 90,
    },
    {
      title: '集群名',
      colKey: 'clusterName',
      ellipsis: true,
      cell: ({ row }) => row.clusterName || '-',
      width: 160,
    },

    {
      title: '请求url',
      colKey: 'url',
      width: 220,
      ellipsis: true,
    },
    {
      title: '请求地址',
      colKey: 'ipPort',
      ellipsis: true,
      width: 220,
    },

    {
      title: '请求参数',
      colKey: 'args',
      ellipsis: true,
      width: 220,
    },
    {
      title: '请求结果',
      colKey: 'returnContent',
      ellipsis: true,
      width: 220,
    },
    {
      title: '操作类型',
      colKey: 'operateType',
      cell: ({ row }) => row.operateType || '-',
      width: 90,
    },
    {
      title: '来源',
      colKey: 'sourceType',
      cell: ({ row }) => row.sourceType || '-',
      width: 90,
    },

    {
      title: '操作时间',
      colKey: 'createTime',
      cell: renderDateTime,
      width: 180,
    },
  ];

  const loadData = useMemoizedFn(async (params) => {
    const { data } = await getLogsList(params); // api分页无效，暂认为假分页
    setData(data.items, data.count);
  });

  const { setData, doSearch, config, header, doRefresh } = useTable({
    columns,
    search: (
      <>
        <FormItem name='startTime'>
          <DatePicker enableTimePicker placeholder='开始时间' allowInput clearable />
        </FormItem>
        <FormItem name='endTime'>
          <DatePicker enableTimePicker placeholder='结束时间' allowInput clearable />
        </FormItem>
        <FormItem name='clusterName'>
          <Input clearable placeholder='请输入集群名' />
        </FormItem>
        <FormItem name='operateType' className='select-collapsed'>
          <SelectCollapsed
            clearable
            minCollapsedNum={2}
            options={toTsOptions(operateTypeOptions)}
            placeholder='选择操作类型'
          />
        </FormItem>
        <FormItem name='sourceType'>
          <Select
            clearable
            filterable
            options={toTsOptions(sourceTypeOptions)}
            placeholder='选择来源'
          />
        </FormItem>
      </>
    ),
    searchExtra: (
      <>
        <FormItem label='用户名' name='userName'>
          <Input clearable placeholder='用户名' />
        </FormItem>
        <FormItem label='操作详情' name='operateDesc'>
          <Input clearable placeholder='输入操作详情' />
        </FormItem>

        <FormItem label='URL' name='url'>
          <Input clearable placeholder='输入URL' />
        </FormItem>
        <FormItem label='IP端口' name='ipPort'>
          <Input clearable placeholder='输入IP端口' />
        </FormItem>
        <FormItem label='返回code' name='returnCode'>
          <Input clearable placeholder='输入返回code' />
        </FormItem>
      </>
    ),
    onSearch: loadData,
    remote: true,
    operators: (
      <PermWrapper resource='pulsar:sys:logs:delete'>
        <Button theme='danger' onClick={() => setVisible(true)}>
          清理
        </Button>
      </PermWrapper>
    ),
  });

  useEffect(() => {
    doSearch();
  }, [doSearch]);

  const handleClose = (refresh) => {
    setVisible(false);
    if (refresh) {
      doRefresh();
    }
  };

  return (
    <Root>
      {header}
      <Table {...config} />
      <FormModal request={deleteLogs} visible={visible} handleClose={handleClose} />
    </Root>
  );
}

const Root = styled.div`
  padding-top: 20px;
  .search-form {
    .t-form__item {
      width: 160px;
      min-width: 160px;
      &.select-collapsed {
        width: auto;
      }
    }
  }
  .search-form-extra .t-form__item {
    width: 300px;
  }
  .t-radio-button.t-is-checked {
    background-color: var(--td-brand-color-7);
    border-radius: 6px;
    .t-radio-button__label {
      color: #fff;
      font-weight: bold;
    }
  }
  .t-radio-group__bg-block {
    background-color: rgba(0, 0, 0, 0.1) !important;
    border-radius: 6px !important;
  }
`;
